import { isNil, throttle } from 'lodash-es';
import { type RefObject, useContext, useEffect } from 'react';

import { AppShellStickyContext } from '../components/layout/AppShellLayoutForSSR';

/**
 * window.scrollY 값이 입력한 target의 높이에 도달하면 앱쉘 sticky를 토글한다.
 * 사파리에서 boundingClientRect로 요소의 Y값을 구하면 지연되어 값을 반환하는 이슈가 있음.
 * @link https://github.com/inflearn/course-frontend/pull/901
 */
export const useToggleAppShellSticky = (ref: RefObject<HTMLElement>, disabled: boolean) => {
  const appShellStickyContext = useContext(AppShellStickyContext);

  const setIsAppShellSticky = appShellStickyContext?.setIsAppShellSticky;

  const handleAppShellSticky = throttle(() => {
    const $target = ref.current;

    if (isNil($target) || !appShellStickyContext || !setIsAppShellSticky) {
      return;
    }

    if (window.scrollY > $target.offsetTop) {
      // $target 아래까지 스크롤 되었을 때 AppShell sticky off
      return setIsAppShellSticky(false);
    }

    return setIsAppShellSticky(true);
  }, 100);

  useEffect(() => {
    if (!setIsAppShellSticky) {
      return;
    }

    if (disabled) {
      setIsAppShellSticky(true);

      return;
    }

    const $target = ref.current;

    if (isNil($target)) {
      return;
    }

    window.addEventListener('scroll', handleAppShellSticky);
    handleAppShellSticky();

    return () => {
      window.removeEventListener('scroll', handleAppShellSticky);
    };
  }, [disabled, handleAppShellSticky, setIsAppShellSticky, ref]);
};
